import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  switchMap,
  distinctUntilChanged,
  debounceTime,
  map,
} from 'rxjs/operators';
import { Ag1TableService } from '../../ag1-table.service';
import { FieldType } from '../../ag1-table.component';
import { ItemType } from '@app/core/components/ag1-dropdown-button/ag1-dropdown-button.component';
import {
  GenerateAg1TableOutputFileGQL,
  GenerateAg1TableOutputFileMutation,
  GenerateAg1TableOutputFileMutationVariables,
  TemplateFileFormatEnum,
} from '@app/generated/graphql';
import { ToastService } from '@app/core/services/toast/toast.service';
import { ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import * as Sentry from '@sentry/browser';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { SentenceCasePipe } from '@app/core/pipes/sentence.pipe';
import { Ag1DropdownButtonComponent } from '../../../ag1-dropdown-button/ag1-dropdown-button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-table-download',
  templateUrl: './ag1-table-download.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Ag1DropdownButtonComponent],
})
export class Ag1TableDownloadComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private apollo = inject(Apollo);
  private generatedAg1TableOutputFileGQL = inject(
    GenerateAg1TableOutputFileGQL,
  );
  private humanizePipe = inject(HumanizePipe);
  private sentenceCasePipe = inject(SentenceCasePipe);
  @Input() tableService: Ag1TableService;
  @Input() tableName: string;
  /** (Optional) If false, the pdf format will not be shown in the dropdown */
  @Input() hasPdfFormat: boolean = true;
  /** (Optional) If false, the excel format will not be shown in the dropdown */
  @Input() hasXlsFormat: boolean = true;

  isLoading: boolean;
  isGenerating: boolean;
  isEmpty: boolean;
  tableData: any[];
  fields: FieldType[] = [];
  filterValues = '';

  constructor() {}

  ngOnInit(): void {
    this.validateInputs();
    this.initializeSubscriptions();
  }

  private validateInputs(): void {
    if (!this.tableName) {
      throw new Error('Ag1TableDownloadComponent: Table name is required');
    }
    if (!this.tableService) {
      throw new Error('Ag1TableDownloadComponent: Table service is required');
    }
  }

  private initializeSubscriptions(): void {
    // Subscribe to query params
    this.route.queryParams
      .pipe(takeUntilDestroyed(this._destroyRef), distinctUntilChanged())
      .subscribe((params) => {
        // we expect something like
        // /pack-instructionsquery=
        // {"packing week":{"startWeek":"2024-W44","endWeek":"2024-W45","locked":true},
        // "sellers":[{"id":3,"label":"Test Farmer"}]}
        try {
          // params.query is the legacy filter param; newer pages use appliedFilters
          this.filterValues = params.appliedFilters || params.query || '';
        } catch (error) {
          Sentry.captureException(error);
          this.filterValues = '';
        }
      });

    // SwitchMap in order to avoid multiple subscriptions
    // DebounceTime to avoid multiple calls in a short period of time
    // DistinctUntilChanged to avoid multiple calls with the same value
    this.tableService.isLoading$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(),
        debounceTime(100),
        switchMap((isLoading: boolean) => {
          this.isLoading = isLoading;

          return this.tableService.data$.pipe(
            distinctUntilChanged(),
            switchMap((data: any[]) => {
              this.tableData = data;
              this.isEmpty = data.length === 0;

              return this.tableService.fields$.pipe(
                distinctUntilChanged(),
                map((fields: FieldType[]) => {
                  this.fields = fields;
                  this.refreshInterface();
                }),
              );
            }),
          );
        }),
      )
      .subscribe();
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  download(selectedFormat: ItemType): void {
    if (
      this.isLoading ||
      this.isGenerating ||
      this.isEmpty ||
      this.tableData.length === 0
    ) {
      return;
    }

    this.isLoading = true;
    this.isGenerating = true;
    this.refreshInterface();

    const variables: GenerateAg1TableOutputFileMutationVariables = {
      data: this.tableData,
      fields: this.mapDisplayNames(this.fields),
      fileformat: selectedFormat.value as TemplateFileFormatEnum,
      tablename: this.tableName,
      filtervalues: this.filterValues,
    };

    this.apollo
      .mutate<GenerateAg1TableOutputFileMutation>({
        mutation: this.generatedAg1TableOutputFileGQL.document,
        variables,
      })
      .subscribe({
        next: (res) => {
          window.open(res.data.generateAg1TableOutputFile.fileUrl, '_blank');
          this.isLoading = false;
          this.isGenerating = false;
          this.refreshInterface();
        },
        error: (error) => {
          this.toastService.showErrorToast(
            'Error: Unable to download table data.',
          );
          console.error(error);
          Sentry.captureException(error);
          this.isLoading = false;
          this.isGenerating = false;
          this.refreshInterface();
        },
      });
  }

  mapDisplayNames(fields: FieldType[]): FieldType[] {
    // if the field has a displayName, use it
    // otherwise, format the name using the humanize pipe and sentence case pipe
    return fields.map((field: FieldType) => {
      const displayName: string =
        field.displayName ||
        this.sentenceCasePipe.transform(
          this.humanizePipe.transform(field.name),
        );

      return {
        ...field,
        displayName,
      };
    });
  }

  get fileFormats(): ItemType[] {
    const fileFormats: ItemType[] = [];

    if (this.hasPdfFormat) {
      fileFormats.push({ label: 'Pdf', value: TemplateFileFormatEnum.Pdf });
    }

    if (this.hasXlsFormat) {
      fileFormats.push({ label: 'Excel', value: TemplateFileFormatEnum.Xls });
    }

    return fileFormats;
  }
}
